import { Link } from "react-router-dom";
import { Info } from "lucide-react";
import { lookupTerm } from "@/lib/glossary";

/**
 * GlossaryTerm — renders a label with an inline ℹ that shows the glossary
 * definition on hover and links through to the full entry in the Guide.
 * If the term isn't found, it renders the label plainly.
 *
 *   <GlossaryTerm k="residual" label="Residual" />
 */
export const GlossaryTerm = ({ k, label, testid }) => {
  const entry = lookupTerm(k || label);
  if (!entry) return <>{label}</>;
  // Prefer the in-depth Help Centre article when a `helpSlug` is set;
  // fall back to the Guide's glossary entry otherwise.
  const href = entry.helpSlug
    ? `/help?slug=${encodeURIComponent(entry.helpSlug)}`
    : `/manual?term=${encodeURIComponent(entry.term)}`;
  const ctaLabel = entry.helpSlug ? "Open in Help Centre →" : "Open in Guide →";
  return (
    <span className="group relative inline-flex items-center gap-1">
      {label}
      <Link
        to={href}
        aria-label={`Define ${entry.term}`}
        data-testid={testid || `glossary-term-${k || label}`}
        className="text-ek-ink/30 transition-colors hover:text-ek-purple"
        onClick={(e) => e.stopPropagation()}
      >
        <Info size={12} className="cursor-help" />
      </Link>
      <span
        role="tooltip"
        className="pointer-events-none absolute bottom-full left-1/2 z-50 mb-1.5 w-60 -translate-x-1/2 rounded-md bg-ek-ink px-3 py-2 text-left text-xs font-normal normal-case leading-relaxed tracking-normal text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100"
      >
        <span className="font-semibold text-white">{entry.term}</span>
        <span className="mt-0.5 block text-white/80">{entry.def}</span>
        <span className="mt-1 block text-ek-lilac">{ctaLabel}</span>
      </span>
    </span>
  );
};
